import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
  SpsBereinigungBericht,
  SpsBereinigungErgebnis,
  SpsBereinigungKonflikt,
  SpsKonfliktLoesung,
  SpsUngeklaerteZuordnung,
  SpsZuordnungAutomatischErgebnis,
  SpsZuordnungKandidat
} from '../../../../../Models/Sps';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';

/**
 * Manuelle Klärung ungeklärter SPS-Signalverknüpfungen eines Projekts.
 *
 * Technische Grundlage: Kandidaten laden, einem Zielsignal zuordnen (mit
 * Konfliktlösung, 409 wird erkannt), als eigenständig bestätigen und die
 * automatische Klärung anstoßen. Die Oberfläche folgt.
 */
@Component({
  selector: 'app-sps-zuordnung',
  templateUrl: './sps-zuordnung.component.html',
  styleUrls: ['./sps-zuordnung.component.css']
})
export class SpsZuordnungComponent implements OnInit, OnDestroy {

  projektId = 0;

  ungeklaerte: SpsUngeklaerteZuordnung[] = [];
  letztesAutomatikErgebnis: SpsZuordnungAutomatischErgebnis | null = null;

  /** Dry-Run-Bericht der Bereinigung doppelter Signale (ändert nichts). */
  bericht: SpsBereinigungBericht | null = null;
  /** Ergebnis der zuletzt angewendeten Bereinigung. */
  bereinigungErgebnis: SpsBereinigungErgebnis | null = null;

  laedt = false;
  arbeitet = false;
  fehler = '';

  /** True nach HTTP 409 beim Zusammenführen: beide Signale sind geprüft, Konfliktlösung nötig. */
  konflikt = false;

  private subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: SpsPruefungApiService
  ) { }

  ngOnInit(): void {
    this.subs.add(
      this.route.paramMap.subscribe(params => {
        this.projektId = Number(params.get('projektId')) || 0;
        this.laden();
      }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }


  // =========================================================
  // LADEN
  // =========================================================

  laden(): void {
    if (!this.projektId) {
      this.fehler = 'Ungültige Projekt-Id.';
      return;
    }

    this.laedt = true;
    this.fehler = '';
    this.konflikt = false;

    this.api.getZuordnungKandidaten(this.projektId)
      .pipe(finalize(() => this.laedt = false))
      .subscribe({
        next: liste => this.ungeklaerte = liste,
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Zuordnungen konnten nicht geladen werden.')
      });
  }


  // =========================================================
  // ENTSCHEIDEN
  // =========================================================

  /** Eintrag dem gewählten Kandidaten zuordnen; bei zwei geprüften Signalen ist `konfliktloesung` nötig. */
  zuordnen(
    zuordnung: SpsUngeklaerteZuordnung,
    kandidat: SpsZuordnungKandidat,
    konfliktloesung: SpsKonfliktLoesung = 'Keine'
  ): void {
    this.arbeitet = true;
    this.fehler = '';
    this.konflikt = false;

    this.api.zuordnungBestaetigen(this.projektId, {
      eintragId: zuordnung.eintragId,
      zielSignalId: kandidat.signalId,
      konfliktloesung
    })
      .pipe(finalize(() => this.arbeitet = false))
      .subscribe({
        next: () => this.laden(),
        error: err => {
          this.konflikt = SpsPruefungApiService.istVersionskonflikt(err);
          this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Zuordnung konnte nicht gespeichert werden.');
        }
      });
  }

  /** Eintrag bleibt ein eigenständiges Signal. */
  eigenstaendig(zuordnung: SpsUngeklaerteZuordnung): void {
    this.arbeitet = true;
    this.fehler = '';

    this.api.zuordnungEigenstaendig(this.projektId, { eintragId: zuordnung.eintragId })
      .pipe(finalize(() => this.arbeitet = false))
      .subscribe({
        next: () => this.laden(),
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Bestätigung konnte nicht gespeichert werden.')
      });
  }

  /** Eindeutige Fälle automatisch klären lassen. */
  automatisch(): void {
    this.arbeitet = true;
    this.fehler = '';

    this.api.zuordnungAutomatisch(this.projektId)
      .pipe(finalize(() => this.arbeitet = false))
      .subscribe({
        next: ergebnis => {
          this.letztesAutomatikErgebnis = ergebnis;
          this.laden();
        },
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Die automatische Zuordnung ist fehlgeschlagen.')
      });
  }


  // =========================================================
  // BEREINIGUNG DOPPELTER SIGNALE
  // =========================================================

  /** Dry-Run laden: zeigt, was sicher zusammengeführt würde und was manuell zu entscheiden ist. Ändert nichts. */
  berichtLaden(): void {
    this.arbeitet = true;
    this.fehler = '';

    this.api.getBereinigungBericht(this.projektId)
      .pipe(finalize(() => this.arbeitet = false))
      .subscribe({
        next: bericht => this.bericht = bericht,
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Der Bereinigungsbericht konnte nicht geladen werden.')
      });
  }

  /** Nur die sicheren Teile des Berichts anwenden – nach ausdrücklicher Bestätigung. */
  bereinigungAnwenden(): void {
    if (!this.bericht) {
      return;
    }

    const text = `${this.bericht.anzahlZusammenfuehrungen} Zusammenführung(en), ${this.bericht.anzahlVerwaisteSignale} verwaiste Signal(e) entfernen, `
      + `${this.bericht.anzahlKlaerungen} Zuordnung(en) bestätigen, ${this.bericht.anzahlNamensanpassungen ?? 0} Anzeigename(n) anpassen. `
      + `${this.bericht.anzahlKonflikte} Konflikt(e) bleiben zur manuellen Entscheidung. Prüfstatus, Bemerkungen und Protokolle bleiben erhalten. Jetzt anwenden?`;

    if (!window.confirm(text)) {
      return;
    }

    this.arbeitet = true;
    this.fehler = '';

    this.api.bereinigungAnwenden(this.projektId)
      .pipe(finalize(() => this.arbeitet = false))
      .subscribe({
        next: ergebnis => {
          this.bereinigungErgebnis = ergebnis;
          this.bericht = null;
          this.laden();
          this.berichtLaden();
        },
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Bereinigung konnte nicht angewendet werden.')
      });
  }

  /** Konflikt manuell lösen: alle Signale des Konflikts in das erste (Ziel) zusammenführen. */
  konfliktZusammenfuehren(konflikt: SpsBereinigungKonflikt, konfliktloesung: SpsKonfliktLoesung): void {
    const [ziel, ...quellen] = konflikt.signalIds;

    if (!ziel || quellen.length === 0) {
      return;
    }

    this.arbeitet = true;
    this.fehler = '';

    this.api.signaleZusammenfuehren(this.projektId, { zielSignalId: ziel, quellSignalIds: quellen, konfliktloesung })
      .pipe(finalize(() => this.arbeitet = false))
      .subscribe({
        next: () => {
          this.laden();
          this.berichtLaden();
        },
        error: err => {
          this.konflikt = SpsPruefungApiService.istVersionskonflikt(err);
          this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Signale konnten nicht zusammengeführt werden.');
        }
      });
  }

  /** Konflikt manuell lösen: die Signale bleiben bewusst getrennt. */
  konfliktGetrenntLassen(konflikt: SpsBereinigungKonflikt): void {
    const [, ...weitere] = konflikt.signalIds;
    const signalId = weitere[0] ?? konflikt.signalIds[0];

    if (!signalId) {
      return;
    }

    this.arbeitet = true;
    this.fehler = '';

    this.api.signalEigenstaendig(this.projektId, signalId)
      .pipe(finalize(() => this.arbeitet = false))
      .subscribe({
        next: () => {
          this.laden();
          this.berichtLaden();
        },
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Trennung konnte nicht gespeichert werden.')
      });
  }

  trackByKonflikt(index: number, k: SpsBereinigungKonflikt): string {
    return `${k.art}:${k.signalIds.join('-')}:${index}`;
  }


  // =========================================================
  // NAVIGATION
  // =========================================================

  oeffneSignal(signalId: number): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-signale', signalId], {
      queryParams: { von: 'zuordnung' }
    });
  }

  zurSpsPruefung(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-pruefung']);
  }

  zurueckZumProjekt(): void {
    void this.router.navigate(['/projekte', this.projektId]);
  }

  trackByZuordnung(_index: number, z: SpsUngeklaerteZuordnung): number {
    return z.eintragId;
  }
}
